// src/admin/AdminLayout.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  DashboardIcon, PackageIcon, TagIcon, TicketIcon, StarIcon,
  ShoppingBagIcon, UsersIcon, MailIcon, BellIcon, SettingsIcon,
  LogOutIcon, ExternalLinkIcon, MenuIcon, CloseIcon, ChevronDownIcon,
  GiftIcon, TruckIcon,
} from './AdminIcons';

function AdminLayout() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  /* ... (Auth Guard, Logout, etc. restent inchangés) ... */
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const stored = localStorage.getItem('user_data');
    if (!token) { navigate('/login'); return; }
    if (stored) { try { setUser(JSON.parse(stored)); } catch {} }
  }, [navigate]);

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_data');
    navigate('/login');
  };

  /* =========================================================
     NAVIGATION
  ========================================================= */
  const NAV_SECTIONS = [
    {
      title: "Vue d'ensemble",
      items: [
        { to: '/admin', icon: DashboardIcon, label: 'Tableau de bord', end: true },
      ],
    },
    {
      title: 'Catalogue',
      items: [
        { to: '/admin/produits',   icon: PackageIcon, label: 'Produits' },
        { to: '/admin/categories', icon: TagIcon,     label: 'Catégories' },
        { to: '/admin/promotions', icon: TicketIcon,  label: 'Promotions' },
        { to: '/admin/offres',     icon: GiftIcon,    label: 'Offres & Pop-ups' },
        { to: '/admin/avis',       icon: StarIcon,    label: 'Avis clients' },
      ],
    },
    {
      title: 'Ventes',
      items: [
        { to: '/admin/commandes', icon: ShoppingBagIcon, label: 'Commandes' },
        { to: '/admin/clients',   icon: UsersIcon,       label: 'Clientes' },
        { to: '/admin/livraison', icon: TruckIcon,       label: 'Frais de livraison' }, // ⬅️ NOUVEAU
      ],
    },
    {
      title: 'Communication',
      items: [
        { to: '/admin/contacts',      icon: MailIcon, label: 'Contacts' },
        { to: '/admin/notifications', icon: BellIcon, label: 'Notifications' },
      ],
    },
    {
      title: 'Système',
      items: [
        { to: '/admin/parametres', icon: SettingsIcon, label: 'Paramètres' },
      ],
    },
  ];

  return (
    <div className={`admin ${sidebarOpen ? 'admin--sidebar-open' : ''}`}>
      <div className="admin__overlay" onClick={() => setSidebarOpen(false)} />

      <aside className="admin__sidebar">
        {/* ... le reste de la sidebar reste inchangé, il utilisera automatiquement la nouvelle NAV_SECTIONS ... */}
        <div className="admin__sidebar-head">
          <Link to="/admin" className="admin__logo">
            <span className="admin__logo-ar">مداد</span>
            <span className="admin__logo-fr">MADAD</span>
            <span className="admin__logo-tag">Administration</span>
          </Link>
          <button className="admin__close" onClick={() => setSidebarOpen(false)}><CloseIcon size={18} /></button>
        </div>

        <nav className="admin__nav">
          {NAV_SECTIONS.map(section => (
            <div key={section.title} className="admin__nav-section">
              <span className="admin__nav-section-title">{section.title}</span>
              {section.items.map(item => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `admin__nav-item ${isActive ? 'is-active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="admin__nav-icon"><Icon size={18} /></span>
                    <span className="admin__nav-label">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="admin__sidebar-footer">
          <Link to="/" className="admin__nav-item" target="_blank" rel="noreferrer">
            <span className="admin__nav-icon"><ExternalLinkIcon size={18} /></span>
            <span className="admin__nav-label">Voir le site</span>
          </Link>
          <button onClick={handleLogout} className="admin__nav-item admin__nav-item--danger">
            <span className="admin__nav-icon"><LogOutIcon size={18} /></span>
            <span className="admin__nav-label">Déconnexion</span>
          </button>
        </div>
      </aside>

      <div className="admin__main">
        <header className="admin__header">
          <button className="admin__burger" onClick={() => setSidebarOpen(true)}><MenuIcon size={22} /></button>
          <div className="admin__header-title">
            <h1>Administration <span className="admin__header-brand">MADAD</span></h1>
          </div>
          <div className="admin__header-user-wrap" ref={userMenuRef}>
            <button className={`admin__header-user ${userMenuOpen ? 'is-open' : ''}`} onClick={() => setUserMenuOpen(v => !v)}>
              <span className="admin__header-avatar">{user?.name?.charAt(0).toUpperCase() || 'A'}</span>
              <span className="admin__header-name">{user?.name || 'Admin'}</span>
              <ChevronDownIcon size={14} className="admin__header-chevron" />
            </button>
            {/* ... menu déroulant user inchangé ... */}
          </div>
        </header>

        <main className="admin__content"><Outlet /></main>
      </div>
    </div>
  );
}

export default AdminLayout;